import './TeamMembersViewDatatable.scss';

import { Cancel, Check, ContentCopy } from '@mui/icons-material';
import { LoadingButton } from '@mui/lab';
import { Chip, IconButton, Stack } from '@mui/material';
import { DataGrid, GridColDef, GridRenderCellParams } from '@mui/x-data-grid';
import { EventStatusEnum } from 'domain/event/EventStatusEnum';
import { PlayerRoleEnum } from 'domain/event/PlayerRoleEnum';
import { PlayerStatusEnum } from 'domain/event/PlayerStatusEnum';
import { PlayerTeam } from 'domain/event/PlayerTeam';
import { PlayerTeamInvitationTypeEnum } from 'domain/event/PlayerTeamInvitationTypeEnum';
import { Translate } from 'domain/translation/Translation.repository';
import { FormEditRoleField } from 'primary/events/modals/ModalTeamEvent';
import { useContextDependency } from 'primary/hooks/useContextDependency';
import { useTranslate } from 'primary/hooks/useTranslate';
import { handleCopyLink } from 'primary/InvitationLink/invitationLink.func';
import { TeamsViewDatatableParametersProps } from 'primary/teams/ResponsiveTeamMembersView';
import React from 'react';

export const getPlayerStatusObject = (player: PlayerTeam, t: Translate) => {
  if (player.invitationLink === null && !player.player) {
    return {
      label: t('team.modal.edit.statusMissingPlayer'),
      state: 'error',
    };
  }
  if (player.invitationLink != null && !player.player) {
    return {
      label: t('team.modal.edit.statusWaitingSubscribe'),
      state: 'warning',
    };
  }
  if (player.status === PlayerStatusEnum.ACCEPTED)
    return {
      label: t('team.modal.edit.playerStatus.ACCEPTED'),
      state: 'warning',
    };
  if (player.status === PlayerStatusEnum.DECLINED)
    return {
      label: t('team.modal.edit.playerStatus.DECLINED'),
      state: 'error',
    };
  return {
    label: t('event.teams.datatable.statusWaiting'),
    state: 'info',
  };
};

const formatPlayersToRowData = (players: PlayerTeam[], t: Translate) => {
  return players
    .sort((p1, p2) => p2.id - p1.id)
    .map((player, index) => ({
      id: player.id,
      name: player?.player?.idPlayer ?? 'N/A',
      statut: getPlayerStatusObject(player, t),
      action: player,
      role: player,
    }));
};

export const TeamMembersViewDatatable = ({
  team,
  event,
  reFetch,
  disabled,
  onDecline,
}: TeamsViewDatatableParametersProps) => {
  const { teamRepository, authRepository } = useContextDependency();
  const t = useTranslate();
  const [isLoading, setIsLoading] = React.useState(false);

  const onClickLeaveTeam = () => () => {
    setIsLoading(true);
    teamRepository
      .leave(team.id)
      .then(() => {
        onDecline?.();
      })
      .finally(() => setIsLoading(false));
  };

  const onClickAccept = () => () => {
    setIsLoading(true);
    teamRepository
      .acceptTeamInvitationEvent(true, team.id, event?.id as number)
      .then(() => {
        reFetch();
      })
      .finally(() => setIsLoading(false));
  };

  const onClickDecline = () => () => {
    setIsLoading(true);
    teamRepository
      .declineTeamInvitationEvent(event?.id as number, team.id)
      .then(() => {
        onDecline();
      })
      .finally(() => setIsLoading(false));
  };

  const onClickExcludePlayer = (playerId: number) => () => {
    setIsLoading(true);
    teamRepository
      .excludePlayer(playerId)
      .then(() => reFetch())
      .finally(() => setIsLoading(false));
  };

  const onClickAcceptJoin = (playerId: number) => () => {
    setIsLoading(true);
    teamRepository
      .acceptTeamInvitationJoin(playerId)
      .then(() => {
        reFetch();
      })
      .finally(() => setIsLoading(false));
  };

  const onClickDeclineJoin = (playerId: number) => () => {
    setIsLoading(true);
    teamRepository
      .declineTeamInvitationJoin(playerId)
      .then(() => {
        reFetch();
      })
      .finally(() => setIsLoading(false));
  };

  const columns: GridColDef[] = [
    {
      field: 'name',
      headerName: t('event.teams.datatable.labelPlayers'),
      flex: 1,
      headerClassName: '-header',
      headerAlign: 'center',
      sortable: false,
    },
    {
      field: 'role',
      headerName: t('event.teams.datatable.labelRole'),
      flex: 1,
      headerClassName: '-header',
      headerAlign: 'center',
      sortable: false,
      renderCell: (value: GridRenderCellParams<PlayerTeam>) => {
        const isCurrentPlayer =
          authRepository.currentUser?.userProfil?.id === value?.value?.player?.idProfil;
        const isCurrentUserChief =
          team.players.find(
            (valPlayer) =>
              valPlayer?.player?.idProfil === authRepository.currentUser?.userProfil.id,
          )?.role === PlayerRoleEnum.CHIEF;

        if (!value.value.player) return <></>;

        if (isCurrentUserChief) {
          return (
            <FormEditRoleField
              disabled={!isCurrentUserChief || (isCurrentUserChief && isCurrentPlayer)}
              key={value.value.player?.idProfil}
              player={value.value.player}
              initialRole={value.value.role}
              team={team}
              onSubmitted={() => {
                reFetch();
              }}
            />
          );
        } else {
          return (
            <span>{t(`enum.playerRole.${value.value?.role as PlayerRoleEnum}`)}</span>
          );
        }
      },
    },
    {
      field: 'statut',
      headerName: t('event.teams.datatable.labelStatut'),
      flex: 1,
      headerClassName: '-header',
      headerAlign: 'center',
      sortable: false,
      renderCell: (
        value: GridRenderCellParams<{
          label: string;
          state: 'success' | 'error' | 'warning';
        }>,
      ) => (
        <Chip
          label={value.value?.label}
          variant="outlined"
          color={value.value ? value.value.state : 'warning'}
        />
      ),
    },
    {
      field: 'action',
      headerName: t('event.teams.datatable.labelActions'),
      headerClassName: '-header',
      headerAlign: 'center',
      sortable: false,
      flex: 2,
      renderCell: (value: GridRenderCellParams<PlayerTeam>) => {
        const isCurrentPlayer =
          authRepository.currentUser?.userProfil?.id === value?.value?.player?.idProfil;
        const missingPlayer = !value.value.player && !value.value.invitationLink;
        const isCurrentUserChief =
          team.players.find(
            (value) =>
              value?.player?.idProfil === authRepository.currentUser?.userProfil.id,
          )?.role === PlayerRoleEnum.CHIEF;
        console.log('VALUE PLAYER', value.value);
        if (!value.value) return <></>;
        if (EventStatusEnum.PUBLISH !== event?.status) {
          return <span>événement démarré, aucune action possible</span>;
        }
        return (
          <div className={'-actions'}>
            {missingPlayer && <></>}
            {isCurrentUserChief && value.value.invitationLink && !value.value.player && (
              <div className={'-copyLink'}>
                <span>{t('event.teams.datatable.copyLink')}</span>
                <IconButton
                  key={value.value.id}
                  onClick={handleCopyLink(value.value.invitationLink)}
                >
                  <ContentCopy />
                </IconButton>
              </div>
            )}
            {isCurrentUserChief &&
              value.value.player &&
              value.value.typeInvitation === PlayerTeamInvitationTypeEnum.JOIN &&
              value.value.status === PlayerStatusEnum.PENDING && (
                <>
                  <LoadingButton
                    variant={'contained'}
                    color={'primary'}
                    onClick={onClickAcceptJoin(value.value.id)}
                    disabled={disabled}
                    loading={isLoading}
                  >
                    <Check />
                  </LoadingButton>
                  <LoadingButton
                    variant={'contained'}
                    color={'secondary'}
                    onClick={onClickDeclineJoin(value.value.id)}
                    disabled={disabled}
                    loading={isLoading}
                  >
                    <Cancel />
                  </LoadingButton>
                </>
              )}
            {isCurrentUserChief &&
              !isCurrentPlayer &&
              value.value.player &&
              value.value.typeInvitation === PlayerTeamInvitationTypeEnum.INVITE &&
              value.value.status === PlayerStatusEnum.PENDING && (
                <LoadingButton
                  variant={'contained'}
                  color={'secondary'}
                  onClick={onClickDeclineJoin(value.value.id)}
                  disabled={disabled}
                  loading={isLoading}
                >
                  <Cancel />
                </LoadingButton>
              )}
            {!isCurrentUserChief &&
              isCurrentPlayer &&
              value.value.player &&
              value.value.typeInvitation === PlayerTeamInvitationTypeEnum.INVITE &&
              value.value.status === PlayerStatusEnum.PENDING && (
                <>
                  <LoadingButton
                    variant={'contained'}
                    color={'primary'}
                    onClick={onClickAccept()}
                    disabled={disabled}
                    loading={isLoading}
                  >
                    <Check />
                  </LoadingButton>
                  <LoadingButton
                    variant={'contained'}
                    color={'secondary'}
                    onClick={onClickDecline()}
                    disabled={disabled}
                    loading={isLoading}
                  >
                    <Cancel />
                  </LoadingButton>
                </>
              )}
            {isCurrentPlayer &&
              !isCurrentUserChief &&
              value.value.status === PlayerStatusEnum.ACCEPTED && (
                <LoadingButton
                  variant={'contained'}
                  color={'secondary'}
                  onClick={onClickLeaveTeam()}
                  title={
                    !isCurrentUserChief
                      ? t('event.teams.datatable.leaveTeamNotice')
                      : undefined
                  }
                  disabled={disabled}
                  loading={isLoading}
                >
                  {t('event.teams.datatable.leaveTeam')}
                </LoadingButton>
              )}
            {!isCurrentPlayer &&
              isCurrentUserChief &&
              value.value.status !== PlayerStatusEnum.PENDING && (
                <>
                  <LoadingButton
                    variant={'contained'}
                    color={'secondary'}
                    onClick={onClickExcludePlayer(value?.value?.id)}
                    disabled={disabled}
                    loading={isLoading}
                  >
                    {t('event.teams.datatable.deletePlayer')}
                  </LoadingButton>
                </>
              )}
          </div>
        );
      },
    },
  ];

  return (
    <div className={'teamsMemberViewDatatable'}>
      <DataGrid
        disableRowSelectionOnClick
        disableColumnFilter
        disableColumnMenu
        disableColumnSelector
        disableDensitySelector
        disableEval
        disableVirtualization
        className={'-parameters'}
        autoHeight
        hideFooter
        sx={{ color: 'white' }}
        components={{
          //@ts-ignore
          noRowsOverlay: () => (
            <Stack height="100%" alignItems="center" justifyContent="center">
              {t('event.teams.datatable.noContentLabel')}
            </Stack>
          ),
        }}
        getRowId={(row) => row.id}
        rows={team.players ? formatPlayersToRowData(team.players, t) : []}
        columns={columns}
      />
    </div>
  );
};
